/**
 * useContactsTable Hook - Server-Side Query Builder
 * 
 * Advanced hook for managing contacts table with full server-side:
 * - Filtering (text, numeric, date, select, array, boolean)
 * - Sorting (any column, asc/desc)
 * - Pagination (true LIMIT/OFFSET with total count)
 * 
 * This replaces client-side filtering/sorting that was necessary with Firebase.
 * 
 * @example
 * const { 
 *   contacts, 
 *   totalCount, 
 *   loading, 
 *   applyFilters, 
 *   applySort, 
 *   setPage 
 * } = useContactsTable({
 *   ownerId: user.uid,
 *   pageSize: 25,
 *   initialFilters: savedSmartListFilters || {},
 * });
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { getSupabase, isSupabaseConfigured, transformContactToCamelCase, fetchAllRows } from '../utils/supabase';
import { applyVisibilityScope, visibilityScopeKey } from '../utils/recordVisibility';
import moment from 'moment';

// Filter operator types
export const FILTER_OPERATORS = {
  // Text operators
  contains: 'contains',
  equals: 'equals',
  starts_with: 'starts_with',
  ends_with: 'ends_with',
  is_empty: 'is_empty',
  is_not_empty: 'is_not_empty',
  // Numeric operators
  eq: 'eq',
  gt: 'gt',
  lt: 'lt',
  gte: 'gte',
  lte: 'lte',
  between: 'between',
  // Date operators
  date_eq: 'date_eq',
  date_before: 'date_before',
  date_after: 'date_after',
  date_between: 'date_between',
  date_relative: 'date_relative', // last 7 days, this month, etc.
  // Select operators
  in: 'in',
  not_in: 'not_in',
  // Array operators
  contains_any: 'contains_any',
  contains_all: 'contains_all',
  not_contains_any: 'not_contains_any',
  array_is_empty: 'array_is_empty',
  // Boolean operators
  is_true: 'is_true',
  is_false: 'is_false',
};

// Relative date presets
export const RELATIVE_DATE_PRESETS = {
  today: { label: 'Today', days: 0 },
  yesterday: { label: 'Yesterday', days: 1 },
  last_7_days: { label: 'Last 7 days', days: 7 },
  last_14_days: { label: 'Last 14 days', days: 14 },
  last_30_days: { label: 'Last 30 days', days: 30 },
  last_90_days: { label: 'Last 90 days', days: 90 },
  this_month: { label: 'This month', type: 'month' },
  last_month: { label: 'Last month', type: 'last_month' },
  this_year: { label: 'This year', type: 'year' },
};

// Map frontend field names to database column names
const FIELD_MAP = {
  name: 'first_name', // Special handling needed for full name search
  firstName: 'first_name',
  lastName: 'last_name',
  middleInitial: 'middle_initial',
  email: 'email',
  phone: 'phone',
  secondaryEmail: 'secondary_email',
  secondaryPhone: 'secondary_phone',
  company: 'company',
  jobTitle: 'job_title',
  source: 'source',
  contactTypes: 'contact_types',
  tags: 'tags',
  activeCampaigns: 'active_campaign_ids',
  isActive: 'is_active',
  isFavorite: 'is_favorite',
  dnc: 'do_not_contact',
  dncCall: 'dnc_call',
  dncEmail: 'dnc_email',
  dncSms: 'dnc_sms',
  createdAt: 'created_at',
  updatedAt: 'updated_at',
  birthday: 'birthday',
  licenseNumber: 'license_number',
  licenseState: 'license_state',
  totalDeals: 'total_deals',
  wonDeals: 'won_deals',
  lifetimeValue: 'lifetime_value',
  lastContactDate: 'last_interaction_at',
  mailingState: 'mailing_state',
  mailingCity: 'mailing_city',
  recordScope: 'record_scope',
  // Column ID mappings (contactsTableColumnDefinitions uses these IDs)
  lastContact: 'last_interaction_at',  // Column ID 'lastContact' maps to last_interaction_at
  types: 'contact_types',              // Column ID 'types' maps to contact_types (not sortable but for filters)
  // RETR Recruiting Intelligence fields
  retrProfileUrl: 'retr_profile_url',
  retrLoType: 'retr_lo_type',
  retrState: 'retr_state',
  retrLoanCount: 'retr_loan_count',
  retrLoanVolume: 'retr_loan_volume',
  retrConvCount: 'retr_conv_count',
  retrVaCount: 'retr_va_count',
  retrFhaCount: 'retr_fha_count',
  retrTotalLoanCount: 'retr_total_loan_count',
  retrTotalLoanVolume: 'retr_total_loan_volume',
  retrAvgLoan: 'retr_avg_loan',
  retrLocation: 'retr_location',
  retrImportedAt: 'retr_imported_at',
  // RETR expanded fields
  retrProfileId: 'retr_profile_id',
  retrNmlsId: 'retr_nmls_id',
  retrLicensedSince: 'retr_licensed_since',
  retrJobTitle: 'retr_job_title',
  retrWorkEmail: 'retr_work_email',
  retrPersonalEmail: 'retr_personal_email',
  retrOfficePhone: 'retr_office_phone',
  retrMobilePhone: 'retr_mobile_phone',
  retrCompanyName: 'retr_company_name',
  retrOfficeLocation: 'retr_office_location',
  retrEmployerStartDate: 'retr_employer_start_date',
  retrBuyerAgentsCount: 'retr_buyer_agents_count',
  retrYearsExperience: 'retr_years_experience',
  retrStateLicensesCount: 'retr_state_licenses_count',
  retrFederalRegistrationsCount: 'retr_federal_registrations_count',
  retrHasPersonality: 'retr_has_personality',
  // Legacy field mappings - may exist in old user preferences
  due: 'created_at', // Fallback - 'due' doesn't exist on contacts
};

// Valid sortable columns in view_contact_summary
const VALID_SORT_COLUMNS = [
  'first_name', 'last_name', 'email', 'phone', 'company', 'job_title',
  'source', 'is_active', 'is_favorite', 'created_at', 'updated_at',
  'birthday', 'total_deals', 'won_deals', 'lifetime_value', 'last_interaction_at',
  'mailing_state', 'mailing_city', 'id', 'owner_id', 'record_scope',
  // RETR fields (sortable for recruiting views)
  'retr_loan_count', 'retr_loan_volume', 'retr_total_loan_count',
  'retr_total_loan_volume', 'retr_avg_loan', 'retr_lo_type', 'retr_state',
  'retr_imported_at', 'retr_nmls_id', 'retr_company_name',
  'retr_buyer_agents_count', 'retr_years_experience',
];

/**
 * Get valid database column for sorting, with fallback
 */
function getValidSortColumn(field) {
  const mappedField = FIELD_MAP[field] || field;
  // Check if the mapped field is valid
  if (VALID_SORT_COLUMNS.includes(mappedField)) {
    return mappedField;
  }
  // Fallback to created_at if field is invalid
  console.warn(`Invalid sort field "${field}" (mapped to "${mappedField}"), falling back to created_at`);
  return 'created_at';
}

/**
 * Calculate date range from relative preset
 */
function getRelativeDateRange(preset) {
  const now = moment();
  
  switch (preset) {
    case 'today':
      return { start: now.startOf('day').toISOString(), end: now.endOf('day').toISOString() };
    case 'yesterday':
      return { 
        start: now.subtract(1, 'day').startOf('day').toISOString(), 
        end: moment().subtract(1, 'day').endOf('day').toISOString() 
      };
    case 'last_7_days':
      return { start: now.subtract(7, 'days').startOf('day').toISOString(), end: moment().endOf('day').toISOString() };
    case 'last_14_days':
      return { start: now.subtract(14, 'days').startOf('day').toISOString(), end: moment().endOf('day').toISOString() };
    case 'last_30_days':
      return { start: now.subtract(30, 'days').startOf('day').toISOString(), end: moment().endOf('day').toISOString() };
    case 'last_90_days':
      return { start: now.subtract(90, 'days').startOf('day').toISOString(), end: moment().endOf('day').toISOString() };
    case 'this_month':
      return { start: now.startOf('month').toISOString(), end: moment().endOf('month').toISOString() };
    case 'last_month':
      return { 
        start: now.subtract(1, 'month').startOf('month').toISOString(), 
        end: moment().subtract(1, 'month').endOf('month').toISOString() 
      };
    case 'this_year':
      return { start: now.startOf('year').toISOString(), end: moment().endOf('year').toISOString() };
    default:
      return null;
  }
}

/**
 * Apply global search to a Supabase query.
 * Splits multi-word queries so each word must match at least one searchable column.
 * Single-word: (first_name ILIKE '%word%' OR last_name ILIKE '%word%' OR ...)
 * Multi-word:  above AND'd for each word, so "David Young" matches first=David + last=Young
 */
function applyGlobalSearch(query, searchTerm) {
  if (!searchTerm || searchTerm.trim().length < 2) return query;

  const words = searchTerm.trim().split(/\s+/).filter(w => w.length >= 1);
  for (const word of words) {
    const term = `%${word}%`;
    query = query.or(
      `first_name.ilike.${term},last_name.ilike.${term},email.ilike.${term},phone.ilike.${term},company.ilike.${term}`
    );
  }
  return query;
}

// Array columns where "empty" means either SQL NULL or an empty array — both
// occur in the data, so both must match. (active_campaign_ids is NOT NULL
// DEFAULT '{}', but the NULL branch is harmless and keeps the set uniform.)
const ARRAY_FIELDS = new Set(['tags', 'contact_types', 'active_campaign_ids']);

// Collapse a burst of realtime row changes into one refetch.
const REALTIME_REFETCH_DEBOUNCE_MS = 500;

/**
 * Quote one element of a Postgres array literal.
 *
 * supabase-js builds array filters by joining values raw (`ov.{a,b,c}`), so a
 * value containing a comma is silently split into two values, and one with
 * leading/trailing whitespace never matches because the array parser trims
 * unquoted elements. Double-quoting each element makes both cases exact.
 */
function quoteArrayElement(value) {
  return `"${String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}

/**
 * Build a fully quoted Postgres array literal, e.g. {"a","b, still b"}
 */
function toArrayLiteral(values) {
  return `{${values.map(quoteArrayElement).join(',')}}`;
}

/**
 * Match rows where an array column holds no values (NULL or empty array).
 */
function applyArrayIsEmpty(query, dbField) {
  return query.or(`${dbField}.is.null,${dbField}.eq.{}`);
}

/**
 * Apply a single filter condition to a Supabase query
 */
function applyFilterToQuery(query, filter) {
  const { field, operator, value } = filter;
  const dbField = FIELD_MAP[field] || field;
  
  // Handle empty/null checks first
  if (operator === FILTER_OPERATORS.is_empty) {
    // On an array column, "is empty" must also match the empty-array rows
    return ARRAY_FIELDS.has(dbField)
      ? applyArrayIsEmpty(query, dbField)
      : query.is(dbField, null);
  }
  if (operator === FILTER_OPERATORS.is_not_empty) {
    // On an array column, an empty array {} is stored data but not a value,
    // so "is not empty" must reject it along with NULL.
    return ARRAY_FIELDS.has(dbField)
      ? query.not(dbField, 'is', null).not(dbField, 'eq', '{}')
      : query.not(dbField, 'is', null);
  }
  if (operator === FILTER_OPERATORS.array_is_empty) {
    return applyArrayIsEmpty(query, dbField);
  }
  
  // Boolean operators take no value input, so they must run before the
  // no-value skip below. The FilterBuilder leaves value as '' for them, which
  // previously made every advanced-builder boolean filter (DNC "Is Yes/No",
  // At Risk, Favorite, ...) a silent no-op.
  if (operator === FILTER_OPERATORS.is_true) {
    return query.eq(dbField, true);
  }
  if (operator === FILTER_OPERATORS.is_false) {
    // NULL-safe: an unset flag (NULL) counts as "No", so exclusion filters
    // like "DNC: SMS is No" keep rows where the flag was never written.
    return query.or(`${dbField}.is.null,${dbField}.eq.false`);
  }
  
  // Skip if no value provided (except for the operators above)
  if (value === undefined || value === null || value === '') {
    return query;
  }
  
  // Text operators
  if (operator === FILTER_OPERATORS.contains) {
    // Special handling for "name" field - search both first and last name
    if (field === 'name') {
      return query.or(`first_name.ilike.%${value}%,last_name.ilike.%${value}%`);
    }
    return query.ilike(dbField, `%${value}%`);
  }
  if (operator === FILTER_OPERATORS.equals) {
    return query.eq(dbField, value);
  }
  if (operator === FILTER_OPERATORS.starts_with) {
    return query.ilike(dbField, `${value}%`);
  }
  if (operator === FILTER_OPERATORS.ends_with) {
    return query.ilike(dbField, `%${value}`);
  }
  
  // Numeric operators
  if (operator === FILTER_OPERATORS.eq) {
    return query.eq(dbField, value);
  }
  if (operator === FILTER_OPERATORS.gt) {
    return query.gt(dbField, value);
  }
  if (operator === FILTER_OPERATORS.lt) {
    return query.lt(dbField, value);
  }
  if (operator === FILTER_OPERATORS.gte) {
    return query.gte(dbField, value);
  }
  if (operator === FILTER_OPERATORS.lte) {
    return query.lte(dbField, value);
  }
  if (operator === FILTER_OPERATORS.between && Array.isArray(value) && value.length === 2) {
    return query.gte(dbField, value[0]).lte(dbField, value[1]);
  }
  
  // Date operators
  if (operator === FILTER_OPERATORS.date_eq) {
    const startOfDay = moment(value).startOf('day').toISOString();
    const endOfDay = moment(value).endOf('day').toISOString();
    return query.gte(dbField, startOfDay).lte(dbField, endOfDay);
  }
  if (operator === FILTER_OPERATORS.date_before) {
    return query.lt(dbField, moment(value).startOf('day').toISOString());
  }
  if (operator === FILTER_OPERATORS.date_after) {
    return query.gt(dbField, moment(value).endOf('day').toISOString());
  }
  if (operator === FILTER_OPERATORS.date_between && Array.isArray(value) && value.length === 2) {
    return query.gte(dbField, moment(value[0]).startOf('day').toISOString())
                .lte(dbField, moment(value[1]).endOf('day').toISOString());
  }
  if (operator === FILTER_OPERATORS.date_relative) {
    const range = getRelativeDateRange(value);
    if (range) {
      return query.gte(dbField, range.start).lte(dbField, range.end);
    }
  }
  
  // Select operators (for enum/dropdown fields)
  if (operator === FILTER_OPERATORS.in && Array.isArray(value) && value.length > 0) {
    return query.in(dbField, value);
  }
  if (operator === FILTER_OPERATORS.not_in && Array.isArray(value) && value.length > 0) {
    return query.not(dbField, 'in', `(${value.map(quoteArrayElement).join(',')})`);
  }
  
  // Array operators (for tags, contactTypes, etc.)
  if (operator === FILTER_OPERATORS.contains_any && value !== undefined && value !== null) {
    const arr = Array.isArray(value) ? value : [value];
    const normalized = arr.map((v) => (typeof v === 'string' ? v.trim() : String(v))).filter(Boolean);
    if (normalized.length > 0) {
      if (field === 'contactTypes') {
        console.log('[useContactsTable] contactTypes filter: dbField=', dbField, 'value (raw)=', value, 'normalized=', normalized);
      }
      // Postgres array overlap - any value in filter matches any value in column.
      // Uses .filter() with a quoted literal because .overlaps() joins unquoted.
      return query.filter(dbField, 'ov', toArrayLiteral(normalized));
    }
  }
  if (operator === FILTER_OPERATORS.contains_all && value !== undefined && value !== null) {
    const arr = Array.isArray(value) ? value : [value];
    const normalized = arr.map((v) => (typeof v === 'string' ? v.trim() : String(v))).filter(Boolean);
    if (normalized.length > 0) {
      // Postgres array contains - column must have all values
      return query.filter(dbField, 'cs', toArrayLiteral(normalized));
    }
  }
  if (operator === FILTER_OPERATORS.not_contains_any && value !== undefined && value !== null) {
    const arr = Array.isArray(value) ? value : [value];
    const normalized = arr.map((v) => (typeof v === 'string' ? v.trim() : String(v))).filter(Boolean);
    if (normalized.length > 0) {
      // Exclude rows overlapping any of the values. NOT(ov) alone would also
      // drop NULL-array rows (NOT NULL is NULL), but a contact with no tags
      // definitionally doesn't have an excluded tag, so keep them via the OR.
      return query.or(`${dbField}.is.null,${dbField}.not.ov.${toArrayLiteral(normalized)}`);
    }
  }
  
  return query;
}

// Realtime: one filtered channel per accessible owner up to this cap; beyond
// it (or for admin "all") a single unfiltered channel is used instead.
const MAX_REALTIME_OWNER_CHANNELS = 10;

/**
 * Main hook for contacts table with server-side operations
 *
 * Visibility: pass `ownerScope` (from useVisibilityScope) for the multi-owner
 * model — owned records, team book-shares, record-level shares, and company
 * records. The legacy `ownerId` param still works as a plain owner_id equality
 * filter for callers that have not migrated; `ownerScope` wins when both are
 * provided.
 */
export function useContactsTable({
  ownerId,
  ownerScope = null,
  pageSize = 25,
  initialFilters = [],
  initialSort = { field: 'createdAt', direction: 'desc' },
  initialPage = 1,
  globalSearchTerm = '',
} = {}) {
  // State
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [currentPageSize, setCurrentPageSize] = useState(pageSize);
  const [filters, setFilters] = useState(initialFilters);
  const [sort, setSort] = useState(initialSort);
  const [searchTerm, setSearchTerm] = useState(globalSearchTerm);
  
  // Refs
  const subscriptionRef = useRef(null);
  const abortControllerRef = useRef(null);
  // Monotonic request counter: only the newest fetch may commit state, so a
  // slow earlier response can never overwrite a newer one.
  const requestIdRef = useRef(0);

  // Stable key for the active visibility scope — refetch/resubscribe when the
  // accessible owners or record-level grants change.
  const ownerScopeKey = useMemo(() => {
    if (ownerScope) {
      return `${visibilityScopeKey(ownerScope)}:g${ownerScope.grantsVersion || 0}`;
    }
    return ownerId ? `legacy:${ownerId}` : 'all';
  }, [ownerScope, ownerId]);

  /**
   * Apply the visibility filter to a query: multi-owner scope when provided,
   * otherwise the legacy single-owner equality (null = admin all-users).
   */
  const applyOwnerVisibility = useCallback((query) => {
    if (ownerScope) {
      return applyVisibilityScope(query, ownerScope);
    }
    if (ownerId) {
      return query.eq('owner_id', ownerId);
    }
    return query;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerScopeKey]);
  
  // Derived values
  const totalPages = useMemo(() => Math.ceil(totalCount / currentPageSize), [totalCount, currentPageSize]);
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;
  
  /**
   * Fetch contacts with current filters, sort, and pagination
   */
  const fetchContacts = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      setError('Supabase is not configured');
      setLoading(false);
      return;
    }
    
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const myRequestId = ++requestIdRef.current;
    const isStale = () => myRequestId !== requestIdRef.current;
    
    const supabase = getSupabase();
    setLoading(true);
    setError(null);
    
    // Debug: log filter state at the start of each fetch
    const contactTypesFilter = filters.find(f => f.field === 'contactTypes');
    const tagsFilter = filters.find(f => f.field === 'tags');
    console.log('[useContactsTable] fetchContacts called', {
      filterCount: filters.length,
      filters: filters.map(f => ({ field: f.field, operator: f.operator, value: f.value })),
      contactTypesFilter: contactTypesFilter ? contactTypesFilter.value : null,
      tagsFilter: tagsFilter ? tagsFilter.value : null,
      ownerId,
      searchTerm,
      sort,
      currentPage,
      currentPageSize,
    });
    
    try {
      // Build base query on view_contact_summary (includes calculated stats)
      let query = supabase
        .from('view_contact_summary')
        .select('*', { count: 'exact' });
      
      // Visibility: multi-owner scope, legacy owner filter, or admin all-users
      query = applyOwnerVisibility(query);
      
      // Apply global search (splits multi-word queries so each word is AND'd)
      query = applyGlobalSearch(query, searchTerm);
      
      // Apply all filter conditions
      for (const filter of filters) {
        // Debug: log each filter application
        const dbField = FIELD_MAP[filter.field] || filter.field;
        if (filter.field === 'contactTypes' || filter.field === 'tags') {
          const normalizedValue = Array.isArray(filter.value) ? filter.value : [filter.value];
          console.log(`[useContactsTable] Applying array filter: field=${filter.field}, dbField=${dbField}, operator=${filter.operator}, value=`, normalizedValue);
        }
        query = applyFilterToQuery(query, filter);
      }
      
      // Apply sorting with validation
      const sortField = getValidSortColumn(sort.field);
      query = query.order(sortField, { ascending: sort.direction === 'asc' });
      
      // Apply pagination
      const offset = (currentPage - 1) * currentPageSize;
      query = query.range(offset, offset + currentPageSize - 1);
      
      // Execute query
      const { data, error: queryError, count } = await query.abortSignal(controller.signal);
      
      // Debug: log query results
      console.log('[useContactsTable] Query result:', {
        count,
        rowCount: data ? data.length : 0,
        queryError: queryError || null,
        firstRowContactTypes: data && data.length > 0 ? data[0].contact_types : '(no rows)',
        firstRowTags: data && data.length > 0 ? data[0].tags : '(no rows)',
      });
      
      if (queryError) throw queryError;
      if (isStale()) return;
      
      // Transform to camelCase for frontend compatibility
      const transformedContacts = (data || []).map(transformContactToCamelCase);
      
      setContacts(transformedContacts);
      setTotalCount(count || 0);
      
    } catch (err) {
      // An aborted request was superseded on purpose; surfacing it would show a
      // spurious error while the newer request is still loading.
      const wasAborted = err.name === 'AbortError' || controller.signal.aborted;
      if (!wasAborted && !isStale()) {
        console.error('[useContactsTable] Error fetching contacts:', err);
        setError(err.message);
      }
    } finally {
      if (!isStale()) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyOwnerVisibility, ownerId, currentPageSize, currentPage, filters, sort, searchTerm]);
  
  // Always-current fetch function, so the realtime subscription can call the
  // latest version without being torn down and rebuilt on every filter change.
  const fetchContactsRef = useRef(fetchContacts);
  useEffect(() => {
    fetchContactsRef.current = fetchContacts;
  }, [fetchContacts]);

  /**
   * Set up Supabase Realtime subscriptions.
   *
   * Keyed on the visibility scope: a burst of row changes is debounced into a
   * single refetch instead of one per event.
   *
   * postgres_changes filters cannot express OR / array-contains, so a scoped
   * view subscribes one owner_id=eq channel per accessible owner (book shares
   * are few). Edits to records that are only visible via a record-level share
   * (shared_with_ids) are not captured live — grant/revoke changes ARE (via
   * useVisibilityScope's crm_record_shares subscription), and normal refetches
   * cover row edits.
   */
  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    
    const supabase = getSupabase();
    
    // Which owners to watch: scope owners, legacy single owner, or everything.
    let watchedOwnerIds = null; // null = unfiltered (admin all-users)
    if (ownerScope && ownerScope.mode === 'scoped') {
      watchedOwnerIds = ownerScope.ownerIds || [ownerScope.targetUid];
    } else if (!ownerScope && ownerId) {
      watchedOwnerIds = [ownerId];
    }
    // Too many owners → fall back to one unfiltered channel.
    if (watchedOwnerIds && watchedOwnerIds.length > MAX_REALTIME_OWNER_CHANNELS) {
      watchedOwnerIds = null;
    }
    
    let debounceTimer = null;
    const onRowChange = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => fetchContactsRef.current(), REALTIME_REFETCH_DEBOUNCE_MS);
    };
    
    const channelSuffix = Math.random().toString(36).slice(2, 8);
    const channels = [];
    if (watchedOwnerIds === null) {
      channels.push(
        supabase
          .channel(`contacts-table-changes-all-${channelSuffix}`)
          .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_contacts' }, onRowChange)
          .subscribe()
      );
    } else {
      watchedOwnerIds.forEach((watchedOwnerId) => {
        channels.push(
          supabase
            .channel(`contacts-table-changes-${watchedOwnerId}-${channelSuffix}`)
            .on(
              'postgres_changes',
              { event: '*', schema: 'public', table: 'crm_contacts', filter: `owner_id=eq.${watchedOwnerId}` },
              onRowChange
            )
            .subscribe()
        );
      });
    }
    
    subscriptionRef.current = channels;
    
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      channels.forEach((channel) => supabase.removeChannel(channel));
      subscriptionRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerScopeKey]);
  
  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);
  
  /**
   * Apply new filters (replaces all existing filters)
   */
  const applyFilters = useCallback((newFilters) => {
    console.log('[useContactsTable] applyFilters called: newFilters=', JSON.stringify(newFilters));
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  }, []);
  
  /**
   * Add a single filter
   */
  const addFilter = useCallback((filter) => {
    setFilters(prev => [...prev, filter]);
    setCurrentPage(1);
  }, []);
  
  /**
   * Remove a filter by index
   */
  const removeFilter = useCallback((index) => {
    setFilters(prev => prev.filter((_, i) => i !== index));
    setCurrentPage(1);
  }, []);
  
  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setFilters([]);
    setCurrentPage(1);
  }, []);
  
  /**
   * Apply sorting
   */
  const applySort = useCallback((field, direction = 'asc') => {
    setSort({ field, direction });
    setCurrentPage(1); // Reset to first page when sort changes
  }, []);
  
  /**
   * Toggle sort direction for a field
   */
  const toggleSort = useCallback((field) => {
    setSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  }, []);
  
  /**
   * Set current page
   */
  const setPage = useCallback((page) => {
    const validPage = Math.max(1, Math.min(page, totalPages || 1));
    setCurrentPage(validPage);
  }, [totalPages]);
  
  /**
   * Go to next page
   */
  const nextPage = useCallback(() => {
    if (hasNextPage) {
      setCurrentPage(prev => prev + 1);
    }
  }, [hasNextPage]);
  
  /**
   * Go to previous page
   */
  const prevPage = useCallback(() => {
    if (hasPrevPage) {
      setCurrentPage(prev => prev - 1);
    }
  }, [hasPrevPage]);
  
  /**
   * Update global search term
   */
  const setGlobalSearch = useCallback((term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  }, []);
  
  /**
   * Update page size
   */
  const setPageSize = useCallback((newSize) => {
    setCurrentPageSize(newSize);
    setCurrentPage(1); // Reset to first page when page size changes
  }, []);
  
  /**
   * Refresh data
   */
  const refresh = useCallback(() => {
    fetchContacts();
  }, [fetchContacts]);
  
  /**
   * Fetch all contact IDs matching current filters (for "Select All" functionality)
   * Uses pagination to bypass Supabase's 1000-row limit.
   * Returns an array of contact IDs.
   */
  const fetchAllIds = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      console.error('Supabase is not configured');
      return [];
    }
    
    try {
      // Use fetchAllRows helper with custom filter function
      const rows = await fetchAllRows('view_contact_summary', 'id', (query) => {
        // Visibility: multi-owner scope, legacy owner filter, or admin all-users
        query = applyOwnerVisibility(query);
        
        // Apply global search (splits multi-word queries so each word is AND'd)
        query = applyGlobalSearch(query, searchTerm);
        
        // Apply all filter conditions
        for (const filter of filters) {
          query = applyFilterToQuery(query, filter);
        }
        
        return query;
      });
      
      return rows.map(row => row.id);
    } catch (err) {
      console.error('Error fetching all contact IDs:', err);
      return [];
    }
  }, [applyOwnerVisibility, filters, searchTerm]);
  
  /**
   * Fetch full contact rows for a list of contact IDs (for export).
   *
   * Unlike the paginated `contacts` state, this returns EVERY requested
   * contact regardless of the current page. IDs are queried in chunks so
   * large selections don't blow past URL length limits.
   *
   * @param {string[]} ids contact IDs to fetch
   * @returns {Promise<Array<Object>>} camelCase contacts sorted by last/first name
   */
  const fetchContactsByIds = useCallback(async (ids) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }
    if (!ids || ids.length === 0) return [];
    
    const supabase = getSupabase();
    const CHUNK_SIZE = 500;
    const allRows = [];
    
    for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
      const chunk = ids.slice(i, i + CHUNK_SIZE);
      let query = supabase
        .from('view_contact_summary')
        .select('*')
        .in('id', chunk);
      // Visibility guard: exports may only return rows the viewer can see
      // (previously this path was completely unscoped).
      query = applyOwnerVisibility(query);
      const { data, error: queryError } = await query;
      if (queryError) throw queryError;
      allRows.push(...(data || []));
    }
    
    const transformed = allRows.map(transformContactToCamelCase);
    transformed.sort((a, b) =>
      (a.lastName || '').localeCompare(b.lastName || '') ||
      (a.firstName || '').localeCompare(b.firstName || '')
    );
    return transformed;
  }, [applyOwnerVisibility]);
  
  return {
    // Data
    contacts,
    totalCount,
    totalPages,
    currentPage,
    pageSize: currentPageSize,
    
    // Status
    loading,
    error,
    hasNextPage,
    hasPrevPage,
    
    // Current state
    filters,
    sort,
    searchTerm,
    
    // Filter actions
    applyFilters,
    addFilter,
    removeFilter,
    clearFilters,
    
    // Sort actions
    applySort,
    toggleSort,
    
    // Pagination actions
    setPage,
    setPageSize,
    nextPage,
    prevPage,
    
    // Search actions
    setGlobalSearch,
    
    // Other actions
    refresh,
    fetchAllIds,
    fetchContactsByIds,
  };
}

export default useContactsTable;
